/* Hook encargado de hacer la consulta a la base y 
traer  el contenido de los body de los vehiculos

Autor: Benjamín León
Fecha: 2024-08-25
*/
import { useState } from 'react'

import { customFetch } from '../utils/customFetch.ts'
import { useAuth } from '../hooks/useAuth'
import Brand from '../interfaces/Brand.ts'
import YapoModel from '../interfaces/YapoModel.ts'
import VehicleBody from '../interfaces/VehicleBody.ts'

import {
  makeUrlYapoBodies,
  makeUrlYapoBrands,
  makeUrlYapoFuel,
  makeUrlYapoGearbox,
  makeUrlYapoLocalization,
  makeUrlYapoModels,
  makeUrlYapoTypes,
  makeUrlYapoYears,
} from './connectionData.tsx'

const useYapoFetcher = () => {
  const [errorYapoBrands, setErrorYapoBrands] = useState<Error | null>(null)
  const [errorYapoModels, setErrorYapoModels] = useState<Error | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [errorYapoTypes, setErrorYapoTypes] = useState<Error | null>(null)
  const [errorYapoBodies, setErrorYapoBodies] = useState<Error | null>(null)
  const [errorYapoYears, setErrorYapoYears] = useState<Error | null>(null)
  const [errorYapoFuel, setErrorYapoFuel] = useState<Error | null>(null)
  const [errorYapoGearbox, setErrorYapoGearbox] = useState<Error | null>(null)
  const [errorYapoLocalization, setErrorYapoLocalization] = useState<Error | null>(null)
  const { userData } = useAuth()

  const yapoModels = async (type: string, brand: string) => {
    setLoading(true)
    const params = new URLSearchParams({
      type: type,
      brand: brand,
    })

    try {
      const apiUrl = makeUrlYapoModels(params)
      const data = await customFetch<YapoModel[]>(apiUrl)
      setErrorYapoModels(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoModels(err)
      } else {
        setErrorYapoModels(new Error('An unknown error occurred'))
      }

      return []
    } finally {
      setLoading(false)
    }
  }
  const yapoBrands = async () => {
    setLoading(true)

    try {
      const apiUrl = makeUrlYapoBrands()
      const data = await customFetch<Brand[]>(apiUrl)
      setErrorYapoBrands(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoBrands(err)
      } else {
        setErrorYapoBrands(new Error('An unknown error occurred'))
      }

      return []
    } finally {
      setLoading(false)
    }
  }

  const yapoBodies = async () => {
    setLoading(true)

    try {
      const apiUrl = makeUrlYapoBodies()
      const data = await customFetch<Record<string, string>>(apiUrl)

      setErrorYapoBodies(null)

      // Eliminamos los que empiezan con "_"
      const filteredBodyTranslate: Record<string, string> = Object.fromEntries(
        Object.entries(data).filter(([key]) => !key.startsWith('_'))
      )
      // Lo pasamos a VehicleBody
      const vehicleBodies: VehicleBody[] = Object.entries(filteredBodyTranslate).map(([key, value]) => ({
        type: 'TYPES.VEHICLE.AUTOMOBILE',
        body: key,
        description: value,
      }))

      // Yapo devuelve SOLO para autos.

      // le agregamos los bodies de las motos. Usamos las de veekls
      const allBodies = vehicleBodies.concat(
        userData?.bodies?.filter(({ type }) => type === 'TYPES.VEHICLE.MOTORCYCLE') ?? []
      )

      return allBodies
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoBodies(err)
      } else {
        setErrorYapoBodies(new Error('An unknown error occurred'))
      }

      return []
    } finally {
      setLoading(false)
    }
  }

  const yapoTypes = async () => {
    setLoading(true)

    try {
      const apiUrl = makeUrlYapoTypes()
      //const data = await customFetch<VehicleType[]>(apiUrl);
      const data = await customFetch<Record<string, string>>(apiUrl)

      setErrorYapoTypes(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoTypes(err)
      } else {
        setErrorYapoTypes(new Error('An unknown error occurred'))
      }

      return {}
    } finally {
      setLoading(false)
    }
  }
  const yapoYears = async () => {
    setLoading(true)

    try {
      const apiUrl = makeUrlYapoYears()
      const data = await customFetch<number[]>(apiUrl)
      setErrorYapoYears(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoYears(err)
      } else {
        setErrorYapoYears(new Error('An unknown error occurred'))
      }

      return []
    } finally {
      setLoading(false)
    }
  }
  const yapoFuel = async () => {
    setLoading(true)

    try {
      const apiUrl = makeUrlYapoFuel()
      const data = await customFetch<Record<string, string>>(apiUrl)
      setErrorYapoFuel(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoFuel(err)
      } else {
        setErrorYapoFuel(new Error('An unknown error occurred'))
      }

      return {}
    } finally {
      setLoading(false)
    }
  }

  const yapoGearbox = async () => {
    setLoading(true)

    try {
      const apiUrl = makeUrlYapoGearbox()
      const data = await customFetch<Record<string, string>>(apiUrl)
      setErrorYapoGearbox(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoGearbox(err)
      } else {
        setErrorYapoGearbox(new Error('An unknown error occurred'))
      }

      return {}
    } finally {
      setLoading(false)
    }
  }
  const yapoLocalization = async () => {
    setLoading(true)

    try {
      const apiUrl = makeUrlYapoLocalization()
      const data = await customFetch<Record<string, string>>(apiUrl)
      setErrorYapoLocalization(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorYapoLocalization(err)
      } else {
        setErrorYapoLocalization(new Error('An unknown error occurred'))
      }

      return {}
    } finally {
      setLoading(false)
    }
  }

  // Exponemos estas variables al exterior
  return {
    loading,
    yapoTypes,
    yapoBodies,
    yapoBrands,
    yapoModels,
    yapoYears,
    yapoFuel,
    yapoGearbox,
    yapoLocalization,
    errorYapoTypes,
    errorYapoBodies,
    errorYapoBrands,
    errorYapoModels,
    errorYapoYears,
    errorYapoFuel,
    errorYapoGearbox,
    errorYapoLocalization,
  }
}

export default useYapoFetcher
